import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  Grid
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { createUserWithMapping, fetchSystemRoles } from '../../../../modules/superadmin';
import { selectCreateUserMappingLoading, selectSystemRoles } from '../../../../modules/superadmin/selectors';

interface Props {
  open: boolean;
  onClose: () => void;
}

const UserModal: React.FC<Props> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const isSaving = useSelector(selectCreateUserMappingLoading);
  const roles = useSelector(selectSystemRoles);

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    system_role_id: ''
  });
  
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (open) {
      dispatch(fetchSystemRoles());
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        system_role_id: ''
      });
    }
  }, [open, dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value) && value !== '') {
        setEmailError('Invalid email format');
      } else {
        setEmailError('');
      }
    }
  };

  const handleSave = () => {
    dispatch(createUserWithMapping({
      ...formData,
      system_role_id: Number(formData.system_role_id)
    }));
  };

  const isFormValid = formData.email && formData.first_name && formData.last_name && formData.system_role_id && !emailError;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Create Super Admin User</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ py: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                required
                size="small"
                error={!!emailError}
                helperText={emailError}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                fullWidth
                required
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                fullWidth
                required
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Phone Number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                label="System Role"
                name="system_role_id"
                value={formData.system_role_id}
                onChange={handleChange}
                fullWidth
                required
                size="small"
              >
                {Array.isArray(roles) && roles.map((role) => (
                  <MenuItem key={role.role_id} value={role.role_id}>
                    {role.role_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#666' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || !isFormValid}
          sx={{
            textTransform: 'none',
            backgroundColor: '#5E8E7E',
            '&:hover': { backgroundColor: '#4A7A6A' }
          }}
        >
          {isSaving ? 'Creating...' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserModal;
